import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, push, set, update, remove } from 'firebase/database';
import * as XLSX from 'xlsx';
import { db, auth } from './firebase';

const BACKEND_URL = 'https://pharmdev-rider.onrender.com';

const CATEGORIES = [
  'Pain Relief','Antibiotics','Malaria','Diabetes','Vitamins',
  'General','Skincare','Baby & Mother','Supplements','Cardiovascular',
  'Respiratory','Digestive','Eye & Ear','Mental Health','Hormones','Other',
];

const FREQUENCY_CODES = [
  { code:'OD',   label:'OD — Once daily' },
  { code:'BD',   label:'BD — Twice daily' },
  { code:'TDS',  label:'TDS — Three times daily' },
  { code:'QDS',  label:'QDS — Four times daily' },
  { code:'QHS',  label:'QHS — Every night at bedtime' },
  { code:'PRN',  label:'PRN — As needed' },
  { code:'STAT', label:'STAT — Immediately (one dose)' },
  { code:'AC',   label:'AC — Before meals' },
  { code:'PC',   label:'PC — After meals' },
];

const COMMISSION_RATE = 0.30;

const inp = {
  width:'100%', padding:'10px 14px', borderRadius:10,
  border:'1px solid #E5E7EB', fontSize:13,
  backgroundColor:'#F7F8FA', outline:'none', boxSizing:'border-box',
};

// PCG/NEPP drug-schedule classification — 'requiresPrescription' stays
// derived from this (drugSchedule !== 'otc') rather than a separate
// checkbox, so nothing that already reads that boolean breaks.
const DRUG_SCHEDULES = [
  { value:'otc',        label:'🟢 OTC — Over the counter' },
  { value:'pom',        label:'🔴 POM — Prescription only' },
  { value:'controlled', label:'⚫ Controlled — e.g. Tramadol, Codeine' },
];

const EMPTY_FORM = {
  name:'', category:'General', pharmacyPrice:'', quantity:'', lowStockThreshold:'10',
  dosageAmount:'', frequency:'OD', timing:'After meals',
  warnings:'', sideEffects:'', drugSchedule:'otc', coldChain:false, inStock:true,
  adultDose:'', childDose:'', substitute:'', substituteNote:'',
};

export default function InventoryTab({ pharmacyId, pharmacyData }) {
  const [medicines, setMedicines]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showForm, setShowForm]             = useState(false);
  const [editingId, setEditingId]           = useState(null);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [search, setSearch]                 = useState('');
  const [catFilter, setCatFilter]           = useState('All');
  const [stockFilter, setStockFilter]       = useState('all');
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [aiLoading, setAiLoading]           = useState(false);
  const [aiResult, setAiResult]             = useState(null);
  const [importing, setImporting]           = useState(false);
  const [updateImporting, setUpdateImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done:0, total:0 });
  const importRef       = useRef();
  const updateImportRef = useRef();

  // ── LOAD ──────────────────────────────────────────────────────────────────
  // pharmacyMedicines/{pharmacyId} is the real source of truth; the global
  // `medicines` scan is only a legacy fallback for pharmacies whose catalog
  // predates that node existing. That fallback used to attach a brand-new
  // onValue(ref(db,'medicines')) — the ENTIRE cross-pharmacy collection —
  // every single time the outer listener re-fired while still empty, and
  // never unsubscribed any of them. Every import writes to pharmacyMedicines
  // AND the global medicines node per row, so mid-import (and on any other
  // pharmacy's unrelated write) those leaked listeners kept firing and could
  // clobber the correct list with a stale/empty one moments after it was set
  // — exactly the "imported medicines disappear" symptom, and worse the more
  // pharmacies are writing to the shared collection concurrently. Tracking
  // the fallback listener and tearing it down the moment real scoped data
  // shows up (which happens on the very first successful import row) keeps
  // at most one alive at a time.
  useEffect(() => {
    if (!pharmacyId) return;
    let unsubFallback = null;
    const unsubscribe = onValue(ref(db, `pharmacyMedicines/${pharmacyId}`), snap => {
      if (snap.exists()) {
        if (unsubFallback) { unsubFallback(); unsubFallback = null; }
        const list = Object.entries(snap.val())
          .map(([k,v]) => ({ id:k, ...v, pharmacyId }))
          .sort((a,b) => (a.name||'').localeCompare(b.name||''));
        setMedicines(list);
        setLoading(false);
        return;
      }
      if (unsubFallback) return;
      unsubFallback = onValue(ref(db,'medicines'), snap2 => {
        if (snap2.exists()) {
          const all = Object.entries(snap2.val())
            .map(([k,v]) => ({ id:k, ...v }))
            .filter(m =>
              m.pharmacyId  === pharmacyId ||
              m.pharmacy    === pharmacyData?.pharmacyName ||
              m.pharmacyUid === pharmacyId
            )
            .sort((a,b) => (a.name||'').localeCompare(b.name||''));
          setMedicines(all);
        } else { setMedicines([]); }
        setLoading(false);
      });
    });
    return () => { unsubscribe(); if (unsubFallback) unsubFallback(); };
  }, [pharmacyId, pharmacyData?.pharmacyName]);

  const upd = (k,v) => setForm(f => ({ ...f, [k]:v }));

  const pharmacyPrice = parseFloat(form.pharmacyPrice) || 0;
  const customerPrice = parseFloat((pharmacyPrice * (1 + COMMISSION_RATE)).toFixed(2));
  const pharmdevEarns = parseFloat((pharmacyPrice * COMMISSION_RATE).toFixed(2));

  // ── READ EXCEL FILE → array of row objects ────────────────────────────────
  // readAsBinaryString is a non-standard FileReader method SheetJS's own
  // docs no longer recommend — inconsistent across browsers, and silently
  // produces garbled/empty rows in some. readAsArrayBuffer + {type:'array'}
  // is the reliable, currently-documented way to read a picked file.
  const readExcel = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type:'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval:'' });
        resolve(rows);
      } catch(err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

  // ── BUILD SAVE DATA FROM ROW ──────────────────────────────────────────────
  const buildMedData = (row) => {
  // Accept every possible column name variation
  const pharmPrice = parseFloat(
    row.pharmacyPrice   || row['pharmacyPrice']    ||
    row.basePrice       || row['basePrice']         ||
    row['Your Price']   || row['Your Price (GH₵)'] ||
    row['your price']   || row['YOUR PRICE']        ||
    row.price           || row.Price                ||
    row.PRICE           || row.cost                 ||
    row.Cost            || row.amount               ||
    row.Amount          || 0
  ) || 0;

  const custPrice = parseFloat((pharmPrice * (1 + COMMISSION_RATE)).toFixed(2));

  const name = String(
    row.name            || row.Name             ||
    row.medicine        || row.Medicine         ||
    row['Medicine Name']|| row['medicine name'] ||
    row['MEDICINE NAME']|| row['Drug Name']     ||
    row['drug name']    || row['Drug']          ||
    row['drug']         || row['Item']          ||
    row['item']         || row['Product']       ||
    row['product']      || row['Description']   ||
    row['description']  || ''
  ).trim();

  const category = String(
    row.category        || row.Category         ||
    row.CATEGORY        || row['Drug Category'] ||
    row['drug category']|| row.type             ||
    row.Type            || 'General'
  ).trim() || 'General';

  const quantity = parseInt(
    row.quantity        || row.Quantity         ||
    row.qty             || row.Qty              ||
    row.QTY             || row.stock            ||
    row.Stock           || row['Stock Qty']     ||
    row['stock qty']    || 0
  ) || 0;

  const frequency = String(
    row.frequency       || row.Frequency        ||
    row.FREQUENCY       || row.freq             ||
    row['Dosage Frequency'] || 'OD'
  ).trim() || 'OD';

  const timing = String(
    row.timing          || row.Timing           ||
    row['When to Take'] || row['when to take']  ||
    row.when            || 'After meals'
  ).trim() || 'After meals';

  const dosageAmount = String(
    row.dosageAmount    || row.dosage           ||
    row.Dosage          || row.dose             ||
    row.Dose            || row['Dosage Amount'] ||
    row['dosage amount']|| ''
  ).trim();

  const warnings = String(
    row.warnings        || row.Warnings         ||
    row.warning         || row.Warning          ||
    row.contraindications || ''
  ).trim();

  const sideEffects = String(
    row.sideEffects     || row['sideEffects']   ||
    row['Side Effects'] || row['side effects']  ||
    row.sides           || ''
  ).trim();

  const adultDose = String(
    row.adultDose       || row['Adult Dose']    ||
    row['adult dose']   || row.adult            ||
    row.Adult           || ''
  ).trim();

  const childDose = String(
    row.childDose       || row['Child Dose']    ||
    row['child dose']   || row.child            ||
    row.Child           || ''
  ).trim();

  const inStock = String(
    row.inStock         || row['In Stock']      ||
    row['in stock']     || row.stock_status     ||
    row.available       || 'true'
  ).toLowerCase() !== 'false';

  const requiresPrescription = String(
    row.requiresPrescription   ||
    row['Requires Prescription']||
    row.prescription           ||
    row.Prescription           ||
    row.rx || row.Rx           || 'false'
  ).toLowerCase() === 'true';

  // A bulk-import CSV predates the OTC/POM/Controlled classification and
  // only ever has the old yes/no Rx column — fall back to 'pom' (the safer
  // assumption for anything flagged as needing a prescription) unless the
  // sheet explicitly has a drugSchedule/Drug Schedule column.
  const rawSchedule = String(
    row.drugSchedule || row['Drug Schedule'] || row['drug schedule'] || ''
  ).toLowerCase().trim();
  const drugSchedule = ['otc', 'pom', 'controlled'].includes(rawSchedule)
    ? rawSchedule
    : (requiresPrescription ? 'pom' : 'otc');

  const coldChain = String(
    row.coldChain || row['Cold Chain'] || row['cold chain'] || 'false'
  ).toLowerCase() === 'true';

  const substitute = String(
    row.substitute      || row.Substitute       ||
    row['Substitute Medicine'] || ''
  ).trim();

  const substituteNote = String(
    row.substituteNote  || row['Substitute Note']||
    row['substitute note'] || ''
  ).trim();

  return {
    name,
    category,
    pharmacyPrice:        pharmPrice,
    customerPrice:        custPrice,
    price:                custPrice,
    commissionAmount:     parseFloat((pharmPrice * COMMISSION_RATE).toFixed(2)),
    commissionRate:       COMMISSION_RATE,
    quantity,
    inStock,
    requiresPrescription: drugSchedule !== 'otc',
    drugSchedule,
    coldChain,
    pharmacyId,
    pharmacyUid:          pharmacyId,
    pharmacy:             pharmacyData?.pharmacyName || '',
    dosage: {
      amount:    dosageAmount,
      frequency,
      timing,
      adult:     adultDose,
      child:     childDose,
    },
    warnings,
    sideEffects,
    substitute:  substitute ? { name: substitute, note: substituteNote } : null,
    updatedAt:   new Date().toISOString(),
  };
};

  // ── IMPORT EXCEL ──────────────────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const rows = await readExcel(file);
      const valid = rows.filter(r => {
  const n = r.name||r.Name||r.medicine||r.Medicine||
    r['Medicine Name']||r['Drug Name']||r['Drug']||
    r['Item']||r['Product']||r['Description']||'';
  return String(n).trim().length > 0;
});
      if (valid.length === 0) { alert('❌ No medicines found. Make sure your Excel has a "name" column.'); setImporting(false); return; }
      setImportProgress({ done:0, total:valid.length });
      for (let i = 0; i < valid.length; i++) {
        const data   = buildMedData(valid[i]);
        const newRef = push(ref(db, `pharmacyMedicines/${pharmacyId}`));
        const full   = { ...data, id:newRef.key, createdAt:new Date().toISOString() };
        await set(newRef, full);
        await set(ref(db, `medicines/${newRef.key}`), full);
        setImportProgress({ done:i+1, total:valid.length });
      }
      alert(`✅ Successfully imported ${valid.length} medicines!`);
    } catch(err) {
      alert('❌ Error reading Excel file. Make sure it is a valid .xlsx or .xls file.');
    } finally { setImporting(false); setImportProgress({ done:0, total:0 }); }
    e.target.value = '';
  };

  // ── UPDATE IMPORT EXCEL ───────────────────────────────────────────────────
  const handleUpdateImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUpdateImporting(true);
    try {
      const rows  = await readExcel(file);
      const valid = rows.filter(r => {
  const n = r.name||r.Name||r.medicine||r.Medicine||
    r['Medicine Name']||r['Drug Name']||r['Drug']||
    r['Item']||r['Product']||r['Description']||'';
  return String(n).trim().length > 0;
});
      if (valid.length === 0) { alert('❌ No medicines found. Make sure your Excel has a "name" column.'); setUpdateImporting(false); return; }

      let updated = 0, added = 0;
      for (const row of valid) {
        const data     = buildMedData(row);
        const existing = medicines.find(m => m.name?.toLowerCase().trim() === data.name.toLowerCase().trim());
        if (existing) {
          await update(ref(db, `pharmacyMedicines/${pharmacyId}/${existing.id}`), data);
          await update(ref(db, `medicines/${existing.id}`), data);
          updated++;
        } else {
          const newRef = push(ref(db, `pharmacyMedicines/${pharmacyId}`));
          const full   = { ...data, id:newRef.key, createdAt:new Date().toISOString() };
          await set(newRef, full);
          await set(ref(db, `medicines/${newRef.key}`), full);
          added++;
        }
      }
      alert(`✅ Update complete!\n\n✏️ Updated: ${updated} medicines\n➕ Added: ${added} new medicines`);
    } catch(err) {
      alert('❌ Error reading Excel file. Make sure it is a valid .xlsx or .xls file.');
    } finally { setUpdateImporting(false); }
    e.target.value = '';
  };

  // ── EXPORT EXCEL ──────────────────────────────────────────────────────────
  const handleExport = () => {
    const rows = medicines.map(m => ({
      'Name':               m.name              || '',
      'Category':           m.category          || '',
      'Your Price (GH₵)':  m.pharmacyPrice      || m.basePrice || 0,
      'Customer Price':     m.customerPrice      || 0,
      'PharmDev Commission':m.commissionAmount   || 0,
      'Quantity':           m.quantity           || 0,
      'In Stock':           m.inStock !== false ? 'true' : 'false',
      'Dosage':             m.dosage?.amount     || m.dosageAmount || '',
      'Frequency':          m.dosage?.frequency  || m.frequency   || '',
      'Timing':             m.dosage?.timing     || m.timing      || '',
      'Adult Dose':         m.dosage?.adult      || m.adultDose   || '',
      'Child Dose':         m.dosage?.child      || m.childDose   || '',
      'Warnings':           m.warnings           || '',
      'Side Effects':       m.sideEffects        || '',
      'Requires Prescription': m.requiresPrescription ? 'true' : 'false',
      'Drug Schedule':      m.drugSchedule || (m.requiresPrescription ? 'pom' : 'otc'),
      'Cold Chain':         m.coldChain ? 'true' : 'false',
      'Substitute':         m.substitute?.name   || '',
      'Substitute Note':    m.substitute?.note   || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
      {wch:30},{wch:18},{wch:16},{wch:16},{wch:20},
      {wch:10},{wch:10},{wch:20},{wch:14},{wch:16},
      {wch:20},{wch:20},{wch:40},{wch:40},{wch:22},
      {wch:24},{wch:24},
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');

    // Add template sheet
    const templateRows = [{
      'Name': 'Paracetamol 500mg',
      'Category': 'Pain Relief',
      'Your Price (GH₵)': 5,
      'Quantity': 100,
      'In Stock': 'true',
      'Dosage': '1-2 tablets',
      'Frequency': 'TDS',
      'Timing': 'After meals',
      'Adult Dose': '1-2 tablets',
      'Child Dose': 'Half tablet under 12',
      'Warnings': 'Do not exceed 8 tablets in 24 hours',
      'Side Effects': 'Rare: skin rash, nausea',
      'Requires Prescription': 'false',
      'Drug Schedule': 'otc',
      'Cold Chain': 'false',
      'Substitute': '',
      'Substitute Note': '',
    }];
    const wsTemplate = XLSX.utils.json_to_sheet(templateRows);
    wsTemplate['!cols'] = ws['!cols'];
    XLSX.utils.book_append_sheet(wb, wsTemplate, 'Template');

    XLSX.writeFile(wb, `PharmDev_Inventory_${pharmacyData?.pharmacyName||'pharmacy'}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ── AI AUTO-FILL ──────────────────────────────────────────────────────────
  // Routed through the backend (see /medicine/ai-analyze in render-backend)
  // instead of calling Anthropic directly — a browser can't call the
  // Anthropic API without a key, and that key must never live in client
  // code where anyone's devtools can read it.
  const analyseWithAI = async () => {
    if (!form.name.trim()) return;
    setAiLoading(true); setAiResult(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${BACKEND_URL}/medicine/ai-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: form.name }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'AI analysis failed');
      upd('category',             result.category            || form.category);
      upd('dosageAmount',         result.dosageAmount        || '');
      upd('frequency',            result.frequency           || 'OD');
      upd('timing',               result.timing              || 'After meals');
      upd('adultDose',            result.adultDose           || '');
      upd('childDose',            result.childDose           || '');
      upd('warnings',             result.warnings            || '');
      upd('sideEffects',          result.sideEffects         || '');
      upd('drugSchedule', result.requiresPrescription ? 'pom' : 'otc');
      setAiResult(result);
    } catch { alert('AI analysis failed. Please fill in details manually.'); }
    finally { setAiLoading(false); }
  };

  // ── OPEN / EDIT ───────────────────────────────────────────────────────────
  const openAdd  = () => { setForm(EMPTY_FORM); setEditingId(null); setAiResult(null); setShowForm(true); };
  const openEdit = (med) => {
    setForm({
      name:                 med.name               || '',
      category:             med.category           || 'General',
      pharmacyPrice:        (med.pharmacyPrice || med.basePrice || med.price || '').toString(),
      quantity:             (med.quantity || '').toString(),
      lowStockThreshold:    (med.lowStockThreshold || 10).toString(),
      dosageAmount:         med.dosage?.amount      || med.dosageAmount || '',
      frequency:            med.dosage?.frequency   || med.frequency    || 'OD',
      timing:               med.dosage?.timing      || med.timing       || 'After meals',
      warnings:             med.warnings            || '',
      sideEffects:          med.sideEffects         || '',
      drugSchedule:         med.drugSchedule || (med.requiresPrescription ? 'pom' : 'otc'),
      coldChain:            med.coldChain || false,
      inStock:              med.inStock !== false,
      adultDose:            med.dosage?.adult       || med.adultDose    || '',
      childDose:            med.dosage?.child       || med.childDose    || '',
      substitute:           med.substitute?.name    || '',
      substituteNote:       med.substitute?.note    || '',
    });
    setEditingId(med.id); setAiResult(null); setShowForm(true);
  };

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim())   { alert('Medicine name is required'); return; }
    if (!form.pharmacyPrice) { alert('Your price is required'); return; }
    setSaving(true);
    try {
      const pharmPrice = parseFloat(parseFloat(form.pharmacyPrice).toFixed(2));
      const custPrice  = parseFloat((pharmPrice*(1+COMMISSION_RATE)).toFixed(2));
      const commission = parseFloat((pharmPrice*COMMISSION_RATE).toFixed(2));
      const data = {
        name:form.name.trim(), category:form.category,
        pharmacyPrice:pharmPrice, customerPrice:custPrice, price:custPrice,
        commissionAmount:commission, commissionRate:COMMISSION_RATE,
        quantity:parseInt(form.quantity)||0, lowStockThreshold:parseInt(form.lowStockThreshold)||10, inStock:form.inStock,
        drugSchedule:form.drugSchedule, requiresPrescription:form.drugSchedule !== 'otc', coldChain:form.coldChain,
        pharmacyId, pharmacyUid:pharmacyId,
        pharmacy:pharmacyData?.pharmacyName||'',
        updatedAt:new Date().toISOString(),
        dosage:{ amount:form.dosageAmount, frequency:form.frequency, timing:form.timing, adult:form.adultDose, child:form.childDose },
        warnings:form.warnings, sideEffects:form.sideEffects,
        substitute:form.substitute?{name:form.substitute,note:form.substituteNote}:null,
      };
      if (editingId) {
        await update(ref(db,`pharmacyMedicines/${pharmacyId}/${editingId}`),data);
        await update(ref(db,`medicines/${editingId}`),data);
      } else {
        const newRef = push(ref(db,`pharmacyMedicines/${pharmacyId}`));
        await set(newRef,{...data,createdAt:new Date().toISOString()});
        await set(ref(db,`medicines/${newRef.key}`),{...data,createdAt:new Date().toISOString()});
      }
      setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); setAiResult(null);
    } catch { alert('Error saving medicine.'); }
    finally { setSaving(false); }
  };

  // ── DELETE / TOGGLE ───────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await remove(ref(db,`pharmacyMedicines/${pharmacyId}/${id}`));
      await remove(ref(db,`medicines/${id}`));
    } catch { alert('Error deleting.'); }
    setDeleteConfirm(null);
  };
  const toggleStock = async (med) => {
    await update(ref(db,`pharmacyMedicines/${pharmacyId}/${med.id}`),{inStock:!med.inStock});
    await update(ref(db,`medicines/${med.id}`),{inStock:!med.inStock});
  };

  const filtered = medicines.filter(m => {
    const ms = !search || m.name?.toLowerCase().includes(search.toLowerCase());
    const mc = catFilter==='All' || m.category===catFilter;
    const mk = stockFilter==='all'||(stockFilter==='instock'?m.inStock!==false:m.inStock===false);
    return ms && mc && mk;
  });

  const inStockCount = medicines.filter(m=>m.inStock!==false).length;
  const outCount     = medicines.filter(m=>m.inStock===false).length;
  const totalValue   = medicines.reduce((s,m)=>s+((m.pharmacyPrice||m.basePrice||0)*(m.quantity||0)),0);
  const lowStockItems = medicines.filter(m => m.inStock!==false && (m.quantity||0) > 0 && (m.quantity||0) <= (m.lowStockThreshold||10));

  return (
    <div>

      {/* VERIFICATION GATE MODAL */}
      {/* IMPORT PROGRESS */}
      {(importing||updateImporting) && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.7)', zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ backgroundColor:'#FFF', borderRadius:16, padding:32, maxWidth:380, width:'90%', textAlign:'center' }}>
            <p style={{ fontSize:40, margin:'0 0 12px' }}>📊</p>
            <h3 style={{ fontWeight:700, margin:'0 0 8px' }}>{updateImporting?'Updating Inventory...':'Importing from Excel...'}</h3>
            {importing && importProgress.total>0 && (
              <>
                <div style={{ backgroundColor:'#F1F3F5', borderRadius:10, overflow:'hidden', margin:'16px 0 8px' }}>
                  <div style={{ height:10, backgroundColor:'#1D9E75', borderRadius:10, width:`${(importProgress.done/importProgress.total)*100}%`, transition:'width 0.3s' }} />
                </div>
                <p style={{ color:'#888', fontSize:14 }}>{importProgress.done} of {importProgress.total} medicines</p>
              </>
            )}
            {updateImporting && <p style={{ color:'#888', fontSize:13, marginTop:12 }}>Matching by name — updating prices, dosages and stock levels...</p>}
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ backgroundColor:'#FFF', borderRadius:16, padding:28, maxWidth:380, width:'90%', textAlign:'center' }}>
            <p style={{ fontSize:40, margin:'0 0 12px' }}>🗑️</p>
            <h3 style={{ fontWeight:700, margin:'0 0 8px' }}>Delete {deleteConfirm.name}?</h3>
            <p style={{ color:'#888', fontSize:14, margin:'0 0 20px' }}>This will be permanently removed from your inventory.</p>
            <div style={{ display:'flex', gap:10 }}>
              <button style={{ flex:1, padding:'11px', border:'1px solid #E5E7EB', borderRadius:10, cursor:'pointer', backgroundColor:'#F7F8FA', color:'#555' }} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
              <button style={{ flex:1, padding:'11px', backgroundColor:'#EF4444', color:'#FFF', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700 }} onClick={()=>handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.6)', zIndex:9998, display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'20px 0' }}>
          <div style={{ backgroundColor:'#FFF', borderRadius:16, padding:28, maxWidth:700, width:'94%', margin:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ fontWeight:700, margin:0, fontSize:17 }}>{editingId?'✏️ Edit Medicine':'➕ Add New Medicine'}</h3>
              <button style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#888' }} onClick={()=>{ setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); setAiResult(null); }}>✕</button>
            </div>

            {/* AI BANNER */}
            {aiResult && (
              <div style={{ backgroundColor:'#F5F3FF', borderRadius:12, padding:14, marginBottom:16, border:'1px solid #DDD6FE', display:'flex', gap:10 }}>
                <span style={{ fontSize:22 }}>🤖</span>
                <div style={{ flex:1 }}>
                  <p style={{ fontWeight:700, fontSize:13, color:'#5B21B6', margin:'0 0 4px' }}>AI Auto-Fill Complete ✅</p>
                  <p style={{ fontSize:12, color:'#6D28D9', margin:0 }}>Dosage, frequency, warnings and side effects filled for <strong>{form.name}</strong>. Review before saving.</p>
                </div>
                <button style={{ background:'none', border:'none', cursor:'pointer', color:'#888', fontSize:16 }} onClick={()=>setAiResult(null)}>✕</button>
              </div>
            )}

            {/* PRICE PREVIEW */}
            {pharmacyPrice > 0 && (
              <div style={{ backgroundColor:'#E8F8F2', borderRadius:12, padding:16, marginBottom:16, border:'1px solid #B2DFD0' }}>
                <p style={{ fontWeight:700, fontSize:13, color:'#1D6B52', margin:'0 0 10px' }}>💡 Price Breakdown</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                  {[
                    {label:'Your Price',    value:`GH₵ ${pharmacyPrice.toFixed(2)}`, color:'#1D9E75', sub:'You receive 100%'},
                    {label:'PharmDev Gets', value:`GH₵ ${pharmdevEarns.toFixed(2)}`, color:'#F59E0B', sub:'30% added on top'},
                    {label:'Customer Pays', value:`GH₵ ${customerPrice.toFixed(2)}`, color:'#3B82F6', sub:'Your price + 30%'},
                  ].map((c,i)=>(
                    <div key={i} style={{ backgroundColor:'#FFF', borderRadius:8, padding:'10px 14px', textAlign:'center', border:'1px solid #B2DFD0' }}>
                      <p style={{ fontSize:11, color:'#888', margin:'0 0 4px' }}>{c.label}</p>
                      <p style={{ fontSize:18, fontWeight:800, color:c.color, margin:0 }}>{c.value}</p>
                      <p style={{ fontSize:10, color:'#888', margin:'2px 0 0' }}>{c.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {/* NAME + AI */}
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Medicine Name *</label>
                <div style={{ display:'flex', gap:10 }}>
                  <input style={{ ...inp, flex:1 }} placeholder="e.g. Paracetamol 500mg" value={form.name} onChange={e=>upd('name',e.target.value)} autoFocus />
                  <button type="button"
                    style={{ padding:'10px 18px', backgroundColor:aiLoading?'#A8D5C2':'#7C3AED', color:'#FFF', border:'none', borderRadius:10, cursor:aiLoading||!form.name.trim()?'not-allowed':'pointer', fontWeight:700, fontSize:13, whiteSpace:'nowrap', opacity:!form.name.trim()?0.6:1 }}
                    onClick={analyseWithAI} disabled={aiLoading||!form.name.trim()}>
                    {aiLoading?'🤖 Analysing...':'🤖 AI Auto-Fill'}
                  </button>
                </div>
                <p style={{ fontSize:11, color:'#888', margin:'4px 0 0' }}>Type the medicine name then click <strong>🤖 AI Auto-Fill</strong> to auto-set dosage, frequency, warnings and side effects.</p>
              </div>

              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Category</label>
                <select style={inp} value={form.category} onChange={e=>upd('category',e.target.value)}>
                  {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Quantity in Stock</label>
                <input style={inp} type="number" placeholder="e.g. 50" value={form.quantity} onChange={e=>upd('quantity',e.target.value)} min="0" />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Low Stock Alert At</label>
                <input style={inp} type="number" placeholder="e.g. 10" value={form.lowStockThreshold} onChange={e=>upd('lowStockThreshold',e.target.value)} min="0" />
                <p style={{ fontSize:11, color:'#888', margin:'4px 0 0' }}>You'll see a warning once quantity drops to this level or below</p>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Your Price (GH₵) *</label>
                <input style={{ ...inp, borderColor:pharmacyPrice>0?'#1D9E75':'#E5E7EB', borderWidth:1.5 }} type="number" placeholder="e.g. 10.00" value={form.pharmacyPrice} onChange={e=>upd('pharmacyPrice',e.target.value)} min="0" step="0.01" />
                {pharmacyPrice>0 && <p style={{ fontSize:12, color:'#1D9E75', margin:'4px 0 0', fontWeight:600 }}>✅ Customer pays GH₵ {customerPrice.toFixed(2)}</p>}
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Customer Price (auto)</label>
                <div style={{ ...inp, backgroundColor:'#E8F8F2', borderColor:'#B2DFD0', color:'#1D6B52', fontWeight:700, fontSize:15, cursor:'not-allowed' }}>
                  {pharmacyPrice>0?`GH₵ ${customerPrice.toFixed(2)}`:'Enter your price first'}
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Dosage Amount</label>
                <input style={inp} placeholder="e.g. 1 tablet, 5ml" value={form.dosageAmount} onChange={e=>upd('dosageAmount',e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Frequency</label>
                <select style={inp} value={form.frequency} onChange={e=>upd('frequency',e.target.value)}>
                  {FREQUENCY_CODES.map(f=><option key={f.code} value={f.code}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>When to Take</label>
                <select style={inp} value={form.timing} onChange={e=>upd('timing',e.target.value)}>
                  {['Before meals','After meals','With meals','On empty stomach','At bedtime','Morning only','Any time'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Adult Dose</label>
                <input style={inp} placeholder="e.g. 1-2 tablets" value={form.adultDose} onChange={e=>upd('adultDose',e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Child Dose</label>
                <input style={inp} placeholder="e.g. Half tablet under 12" value={form.childDose} onChange={e=>upd('childDose',e.target.value)} />
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Warnings / Contraindications</label>
                <textarea style={{ ...inp, minHeight:70, resize:'vertical', fontFamily:'inherit' }} placeholder="e.g. Avoid alcohol. Do not use if allergic to penicillin." value={form.warnings} onChange={e=>upd('warnings',e.target.value)} />
              </div>
              <div style={{ gridColumn:'1 / -1' }}>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Side Effects</label>
                <textarea style={{ ...inp, minHeight:60, resize:'vertical', fontFamily:'inherit' }} placeholder="e.g. Nausea, dizziness, stomach upset" value={form.sideEffects} onChange={e=>upd('sideEffects',e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Substitute (if out of stock)</label>
                <input style={inp} placeholder="e.g. Ibuprofen 400mg" value={form.substitute} onChange={e=>upd('substitute',e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Substitute Note</label>
                <input style={inp} placeholder="e.g. Same effect, different brand" value={form.substituteNote} onChange={e=>upd('substituteNote',e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#444', marginBottom:6 }}>Drug Schedule (PCG/NEPP)</label>
                <select style={inp} value={form.drugSchedule} onChange={e=>upd('drugSchedule',e.target.value)}>
                  {DRUG_SCHEDULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', gap:20 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.inStock} onChange={e=>upd('inStock',e.target.checked)} style={{ width:16, height:16, accentColor:'#1D9E75' }} />
                  <span style={{ fontSize:13, fontWeight:600, color:'#444' }}>✅ In Stock</span>
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={form.coldChain} onChange={e=>upd('coldChain',e.target.checked)} style={{ width:16, height:16, accentColor:'#3B82F6' }} />
                  <span style={{ fontSize:13, fontWeight:600, color:'#444' }}>❄️ Cold Chain</span>
                </label>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:24 }}>
              <button style={{ flex:1, padding:'12px', border:'1px solid #E5E7EB', borderRadius:10, cursor:'pointer', backgroundColor:'#F7F8FA', color:'#555' }} onClick={()=>{ setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); setAiResult(null); }}>Cancel</button>
              <button style={{ flex:2, padding:'12px', backgroundColor:saving?'#A8D5C2':'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:saving?'not-allowed':'pointer', fontWeight:700, fontSize:14 }} onClick={handleSave} disabled={saving}>
                {saving?'Saving...':editingId?'✓ Save Changes':`✓ Add Medicine${customerPrice>0?` — Customer pays GH₵ ${customerPrice.toFixed(2)}`:''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAGE HEADER */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontWeight:700, margin:0, fontSize:18 }}>💊 Medicine Inventory</h2>
          <p style={{ color:'#888', fontSize:13, margin:'4px 0 0' }}>Set your price — PharmDev adds 30% on top. You always receive your full price.</p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>

          {/* IMPORT EXCEL */}
          <label
            style={{ padding:'9px 14px', backgroundColor:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:6 }}
            onClick={e => { e.preventDefault(); importRef.current?.click(); }}
          >
            📥 Import Excel
            <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }} onChange={handleImport} />
          </label>

          {/* UPDATE IMPORT */}
          <label
            style={{ padding:'9px 14px', backgroundColor:'#FEF3C7', color:'#92400E', border:'1px solid #FCD34D', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:6 }}
            onClick={e => { e.preventDefault(); updateImportRef.current?.click(); }}
          >
            🔄 Update Import
            <input ref={updateImportRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }} onChange={handleUpdateImport} />
          </label>

          {/* EXPORT EXCEL */}
          <button style={{ padding:'9px 14px', backgroundColor:'#F0FDF4', color:'#15803D', border:'1px solid #86EFAC', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:13 }} onClick={handleExport}>
            📤 Export Excel
          </button>

          {/* ADD MEDICINE */}
          <button style={{ padding:'9px 18px', backgroundColor:'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }} onClick={openAdd}>
            + Add Medicine
          </button>
        </div>
      </div>

      {/* LOW STOCK BANNER */}
      {lowStockItems.length > 0 && (
        <div style={{ backgroundColor:'#FEF3C7', borderRadius:12, padding:14, marginBottom:20, border:'1px solid #FCD34D', display:'flex', gap:12 }}>
          <span style={{ fontSize:22 }}>⚠️</span>
          <div>
            <p style={{ fontWeight:700, fontSize:13, color:'#92400E', margin:'0 0 4px' }}>
              {lowStockItems.length} medicine{lowStockItems.length>1?'s':''} running low
            </p>
            <p style={{ fontSize:13, color:'#92400E', margin:0, lineHeight:1.6 }}>
              {lowStockItems.slice(0,6).map(m=>m.name).join(', ')}{lowStockItems.length>6?` +${lowStockItems.length-6} more`:''} — restock soon so you don't have to mark them out of stock.
            </p>
          </div>
        </div>
      )}

      {/* INFO BANNER */}
      <div style={{ backgroundColor:'#EFF6FF', borderRadius:12, padding:14, marginBottom:20, border:'1px solid #BFDBFE', display:'flex', gap:12 }}>
        <span style={{ fontSize:22 }}>ℹ️</span>
        <div>
          <p style={{ fontWeight:700, fontSize:13, color:'#1D4ED8', margin:'0 0 4px' }}>How pricing works</p>
          <p style={{ fontSize:13, color:'#555', margin:0, lineHeight:1.6 }}>
            You set <strong>your price</strong>. PharmDev automatically adds <strong>30%</strong> on top.
            You receive <strong>100% of your price</strong> on every order. <strong>Example:</strong> You set GH₵ 10 → Customer pays GH₵ 13 → You get GH₵ 10 → PharmDev gets GH₵ 3.
            <br /><span style={{ color:'#1D4ED8' }}>📊 Use <strong>Export Excel</strong> to download a template, fill it in Excel, then <strong>Import Excel</strong> to upload all medicines at once.</span>
          </p>
        </div>
      </div>

      {/* STATS */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:20 }}>
        {[
          {label:'Total Medicines', value:medicines.length,               color:'#1D9E75', icon:'💊'},
          {label:'In Stock',        value:inStockCount,                   color:'#10B981', icon:'✅'},
          {label:'Low Stock',       value:lowStockItems.length,           color:'#F59E0B', icon:'⚠️'},
          {label:'Out of Stock',    value:outCount,                       color:'#EF4444', icon:'🚫'},
          {label:'Inventory Value', value:`GH₵ ${totalValue.toFixed(0)}`, color:'#7C3AED', icon:'💰'},
        ].map((st,i)=>(
          <div key={i} style={{ backgroundColor:'#FFF', borderRadius:10, padding:14, border:'1px solid #E5E7EB', textAlign:'center', borderTop:`3px solid ${st.color}` }}>
            <p style={{ fontSize:22, margin:'0 0 6px' }}>{st.icon}</p>
            <p style={{ fontSize:20, fontWeight:800, color:st.color, margin:'0 0 2px' }}>{st.value}</p>
            <p style={{ fontSize:12, color:'#555', margin:0, fontWeight:500 }}>{st.label}</p>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input style={{ ...inp, width:240 }} placeholder="Search medicines..." value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={{ ...inp, width:180 }} value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
          <option value="All">All Categories</option>
          {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ display:'flex', backgroundColor:'#F1F3F5', borderRadius:10, padding:4 }}>
          {[{id:'all',label:'All'},{id:'instock',label:'✅ In Stock'},{id:'out',label:'⚠️ Out'}].map(f=>(
            <button key={f.id} style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:stockFilter===f.id?700:400, backgroundColor:stockFilter===f.id?'#FFF':'transparent', color:stockFilter===f.id?'#1D9E75':'#888' }} onClick={()=>setStockFilter(f.id)}>{f.label}</button>
          ))}
        </div>
        {(search||catFilter!=='All'||stockFilter!=='all') && (
          <button style={{ padding:'7px 12px', backgroundColor:'#FEE2E2', color:'#B91C1C', border:'none', borderRadius:8, cursor:'pointer', fontSize:12 }} onClick={()=>{ setSearch(''); setCatFilter('All'); setStockFilter('all'); }}>✕ Clear</button>
        )}
      </div>

      {/* TABLE */}
      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#888' }}>Loading inventory...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, backgroundColor:'#FFF', borderRadius:12, border:'1px solid #E5E7EB' }}>
          <p style={{ fontSize:40, marginBottom:12 }}>💊</p>
          <p style={{ fontWeight:700, color:'#333', fontSize:15, margin:'0 0 6px' }}>{medicines.length===0?'No medicines yet':'No medicines match your search'}</p>
          <p style={{ fontSize:13, color:'#888', margin:'0 0 20px' }}>{medicines.length===0?'Click + Add Medicine or Import Excel to get started':'Try a different search or filter'}</p>
          {medicines.length===0 && (
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button style={{ padding:'11px 20px', backgroundColor:'#1D9E75', color:'#FFF', border:'none', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }} onClick={openAdd}>+ Add Medicine</button>
              <label
                style={{ padding:'11px 20px', backgroundColor:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}
                onClick={e => { e.preventDefault(); importRef.current?.click(); }}
              >
                📥 Import Excel
              </label>
            </div>
          )}
        </div>
      ) : (
        <div style={{ backgroundColor:'#FFF', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                {['Medicine','Category','Your Price','Customer Pays','PharmDev Gets','Qty','Dosage','Frequency','Stock','Actions'].map(h=>(
                  <th key={h} style={{ padding:'12px 14px', textAlign:'left', fontWeight:600, color:'#555', borderBottom:'1px solid #E5E7EB', backgroundColor:'#F7F8FA', whiteSpace:'nowrap', fontSize:12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((med,i) => {
                const pharmPrice = med.pharmacyPrice || med.basePrice || med.price || 0;
                const custPrice  = med.customerPrice  || parseFloat((pharmPrice*1.30).toFixed(2));
                const commission = parseFloat((pharmPrice*COMMISSION_RATE).toFixed(2));
                const dosage     = med.dosage?.amount    || med.dosageAmount || '—';
                const frequency  = med.dosage?.frequency || med.frequency    || '—';
                return (
                  <tr key={med.id} style={{ backgroundColor:i%2===0?'#FFF':'#FAFAFA' }}>
                    <td style={{ padding:'12px 14px' }}>
                      <p style={{ fontWeight:700, color:'#1A1A1A', margin:'0 0 3px' }}>{med.name}</p>
                      {med.drugSchedule === 'controlled' ? (
                        <span style={{ fontSize:10, backgroundColor:'#1A1A1A', color:'#FFF', padding:'1px 6px', borderRadius:20, fontWeight:600 }}>⚫ Controlled</span>
                      ) : med.drugSchedule === 'pom' || med.requiresPrescription ? (
                        <span style={{ fontSize:10, backgroundColor:'#FEE2E2', color:'#B91C1C', padding:'1px 6px', borderRadius:20, fontWeight:600 }}>🔴 POM</span>
                      ) : (
                        <span style={{ fontSize:10, backgroundColor:'#DCFCE7', color:'#15803D', padding:'1px 6px', borderRadius:20, fontWeight:600 }}>🟢 OTC</span>
                      )}
                      {med.coldChain && <span style={{ fontSize:10, backgroundColor:'#EFF6FF', color:'#1D4ED8', padding:'1px 6px', borderRadius:20, fontWeight:600 }}>❄️ Cold</span>}
                      {med.warnings && <p style={{ fontSize:10, color:'#888', margin:'2px 0 0' }}>⚠️ Has warnings</p>}
                    </td>
                    <td style={{ padding:'12px 14px' }}>
                      <span style={{ backgroundColor:'#E8F8F2', color:'#1D6B52', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600 }}>{med.category}</span>
                    </td>
                    <td style={{ padding:'12px 14px', fontWeight:700, color:'#1D9E75', fontSize:14 }}>
                      GH₵ {parseFloat(pharmPrice).toFixed(2)}
                      <p style={{ fontSize:10, color:'#888', margin:'2px 0 0', fontWeight:400 }}>You receive</p>
                    </td>
                    <td style={{ padding:'12px 14px', fontWeight:700, color:'#3B82F6', fontSize:14 }}>
                      GH₵ {custPrice.toFixed(2)}
                      <p style={{ fontSize:10, color:'#888', margin:'2px 0 0', fontWeight:400 }}>Customer pays</p>
                    </td>
                    <td style={{ padding:'12px 14px', color:'#F59E0B', fontWeight:600 }}>
                      GH₵ {commission.toFixed(2)}
                      <p style={{ fontSize:10, color:'#888', margin:'2px 0 0', fontWeight:400 }}>30% markup</p>
                    </td>
                    <td style={{ padding:'12px 14px', fontWeight:600, color:(med.quantity||0)<=(med.lowStockThreshold||10)?'#EF4444':'#1A1A1A' }}>
                      {med.quantity||0}
                      {(med.quantity||0)>0 && (med.quantity||0)<=(med.lowStockThreshold||10) && <p style={{ fontSize:10, color:'#EF4444', margin:'2px 0 0' }}>⚠️ Low (alert at {med.lowStockThreshold||10})</p>}
                    </td>
                    <td style={{ padding:'12px 14px', color:'#555', fontSize:12, maxWidth:130 }}>{dosage}</td>
                    <td style={{ padding:'12px 14px' }}>
                      <span style={{ backgroundColor:'#EDE9FE', color:'#5B21B6', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{frequency}</span>
                    </td>
                    <td style={{ padding:'12px 14px' }}>
                      <button style={{ padding:'5px 12px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, fontWeight:700, backgroundColor:med.inStock!==false?'#DCFCE7':'#FEE2E2', color:med.inStock!==false?'#15803D':'#B91C1C' }} onClick={()=>toggleStock(med)}>
                        {med.inStock!==false?'✅ In Stock':'⚠️ Out'}
                      </button>
                    </td>
                    <td style={{ padding:'12px 14px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <button style={{ padding:'5px 12px', backgroundColor:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600 }} onClick={() => openEdit(med)}>✏️ Edit</button>
                        <button style={{ padding:'5px 10px', backgroundColor:'#FEE2E2', color:'#B91C1C', border:'1px solid #FCA5A5', borderRadius:8, cursor:'pointer', fontSize:12 }} onClick={() => setDeleteConfirm(med)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}